import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  Log,
  User,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client';
import { BehaviorSubject, of } from 'rxjs';
import { Observable } from 'rxjs';
import {
  ALLOW_LAST_LOGIN,
  APPNAME,
  BASE_PATH,
  DEBUG_FLAG,
  IDENTITY_URL,
  POPUP_LOGIN,
} from '../../app.GlobalSettings';
import { DefaultDataService } from './default-data.service';
import { NavigationService } from './navigation.service';
import { AutoLogoutService } from './auto-logout.service';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from '../../components/messageBox/messageBox.component';
import { UserService } from './user.service';
import { catchError, take } from 'rxjs/operators';
import { forkJoin, Subscription } from 'rxjs';
import { AutoLogoutWarningMsgBoxComponent } from 'app/components/autoLogoutWarningMsgBox/autoLogoutWarningMsgBox.component';
import { detect } from 'detect-browser';
import { AuditService } from './audit.service';
import { StatisticsService } from './statistics.service';
import { SignalRService } from './signalr.service';
import { environment } from 'environments/environment';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { EventService } from './event.service';
import { FeedbackService } from './feedback.service';
import { BreakpointObserver } from '@angular/cdk/layout';

// tslint:disable-next-line:max-classes-per-file
@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private manager: UserManager;
  private user: User = null;
  private PopupLoginPrompts = false;
  public AfterLoginNavigateTo = 'dashboard';
  private loggedIn: BehaviorSubject<boolean>;
  private userDisplayName: BehaviorSubject<string>;
  private defaultDataService: DefaultDataService;
  // tslint:disable-next-line:no-inferrable-types
  private bUserLoggedIn: boolean = false;
  // tslint:disable-next-line:no-inferrable-types
  private basePath: string = '';
  private instanceId: string;

  private autoLogout: Subscription;
  private logoutWarning: Subscription;
  private bLogEnabled = false;
  private browser: any;

  private CurrentUserName = '';
  private bIsIntecUser = false;
  private Modules: string[] = [];
  private CustomerSSOAlias: string = '';
  private EnableSignalR: boolean = false;
  private isMobile: boolean;
  private defaultDataLoaded = false;
  private defaultDataRequested = false;

  constructor(
    private feedbackService: FeedbackService,
    private router: Router,
    @Inject(IDENTITY_URL) IdentityUrl: string,
    @Inject(DEBUG_FLAG) debug: boolean,
    @Inject(POPUP_LOGIN) popupLogin: boolean,
    @Inject(BASE_PATH) base_path: string,
    @Inject(ALLOW_LAST_LOGIN) allowLastLogin: boolean,
    private breakpointObserver: BreakpointObserver,
    private defaultDataServiceRef: DefaultDataService,
    private navigationService: NavigationService,
    private dialog: MatDialog,
    private userService: UserService,
    private autoLogoutService: AutoLogoutService,
    private auditService: AuditService,
    private statisticsService: StatisticsService,
    private signalRService: SignalRService,
    private eventService: EventService
  ) {
    this.bLogEnabled = debug;
    this.bLogEnabled = true;
    Log.logger = console;
    Log.level = Log.DEBUG;

    this.browser = detect();

    this.basePath = base_path.toLowerCase().replace('https://', '');
    this.defaultDataService = defaultDataServiceRef;
    this.loggedIn = new BehaviorSubject<boolean>(false);
    this.userDisplayName = new BehaviorSubject<string>('');
    this.PopupLoginPrompts = popupLogin;
    this.manager = new UserManager(
      getClientSettings(IdentityUrl, debug, allowLastLogin)
    );
    // this.manager.events.addAccessTokenExpired((e) => {
    //   this.log('Access Token Expired', e);
    //   this.refreshToken();
    // });

    // this.manager.events.addAccessTokenExpiring((e) => {
    //   this.log('Access Token Expiring', e);
    //   this.refreshToken();
    // });
    this.manager.events.addSilentRenewError((e) => {
      this.log('Silent Renew Error : ', e);
    });
    this.manager.events.addUserLoaded((newUser) => {
      if (newUser) {
        this.log('New Access Token : ', newUser);
        this.manager.getUser().then((newUser2) => {
          if (newUser2) {
            if (this.CurrentUserName === '') {
              this.CurrentUserName = this.getUserEmailFromProfile(
                newUser2.profile
              );
            } else {
              if (
                this.getUserEmailFromProfile(newUser2.profile) !==
                this.CurrentUserName
              ) {
                // Force sign out with message
                const info = new MessageBoxParameters();
                info.title = 'Error';
                info.message = `Another user has logged in in the same web browser which is not allowed. You will now be logged out.`;
                this.dialog
                  .open(MessageBoxComponent, { data: info })
                  .afterClosed()
                  .subscribe((d) => {
                    // Log user out of application as they have no permissions to use it.
                    this.logout(false);
                    return;
                  });
              }
            }
            // Update existing local var with new user data as token may have changed
            this.log('New User loaded after New Access Token', newUser2);
            this.userLoggedIn(newUser2);
          }
        });
      }
    });
    this.manager.events.addUserSignedOut(() => {
      this.log('User Signed Out');
    });
    this.manager.events.addUserUnloaded(() => {
      this.log('User Unloaded');
      this.manager.revokeAccessToken();
    });

    this.autoLogout = this.autoLogoutService.autoLogoutTriggered.subscribe(
      (x: boolean) => {
        if (x === true) {
          this.log('AutoLogout Time out - Need to kick user out.', x);
          this.router.navigate(['/logout']);
        }
      }
    );

    this.logoutWarning =
      this.autoLogoutService.showAutoLogoutWarningDlg.subscribe(
        (secondsBeforeLogout: number) => {
          if (secondsBeforeLogout > 0) {
            // Show message to user then log user out.
            this.log('Auto Logout Warning - Showing dialog...');
            this.dialog
              .open(AutoLogoutWarningMsgBoxComponent, {
                data: secondsBeforeLogout,
              })
              .afterClosed()
              .subscribe((d) => {
                this.log('User clicked and that should reset the timer.');
              });
          }
        }
      );
    if (
      environment.production == false ||
      IdentityUrl.toLocaleLowerCase().indexOf(
        'https://intecid.azurewebsites.net'
      ) > -1
    ) {
      console.log(
        'Checking user login state and auto login if possible (FOR HMR)'
      );
      this.manager.getUser().then((authUser) => {
        if (authUser && authUser.expires_in > 0) {
          console.log('user login state ok to proceed.');
          this.userLoggedIn(authUser, true);
        } else {
          console.log('User Login state expired. Try Silent login');
          this.manager.signinSilent().then((authUser) => {
            // PJT - This is not required as the completeAuthentication gets called when login completes
            // if (authUser) {
            //   console.log('Silent Login done ok', authUser);
            //   this.userLoggedIn(authUser, true);
            // }
          });
        }
      });
    }
  }

  ngOnDestroy(): void { }

  isLoggedIn(): Observable<boolean> {
    return this.loggedIn.asObservable();
  }

  isUserLoggedIn(): boolean {
    return this.bUserLoggedIn;
  }

  userName(): Observable<string> {
    return this.userDisplayName.asObservable();
  }

  getPrivs(): string {
    if (this.isUserLoggedIn()) {
      const claim = this.getClaim('INCASE365.AAB-Privs');
      if (claim) {
        const sysadmin =
          this.bIsIntecUser && claim.toLowerCase().includes('admin')
            ? ',sysadmin'
            : '';
        return this.getClaim('INCASE365.AAB-Privs') + sysadmin;
      }
    }
    return '';
  }

  isCustomerUsingSSO(): boolean {
    return this.CustomerSSOAlias && this.CustomerSSOAlias.length > 0;
  }

  getCustomerSSOAlias(): string {
    return this.CustomerSSOAlias;
  }

  hasModuleLicence(moduleName: string): boolean {
    if (!this.Modules || this.Modules.length < 1) return false;

    var rc = false;
    this.Modules.forEach((i) => {
      if (i.toLocaleLowerCase() == moduleName.toLocaleLowerCase()) rc = true;
    });
    return rc;
  }

    // pass a comma separated list of privs.  Retuurn true of any match privs held by the currently logged in user.
    hasPriv(requiredPrivNames: string): boolean {
      if (requiredPrivNames === undefined) return false;
      if (this.isUserLoggedIn() === false) {
        return false;
      }
      if (this.getClaim('INCASE365.AAB-Privs') === undefined) {
        return false;
      }
      const currentUserPrivs = this.getPrivs().trim().toLowerCase() || 'user';
      return AuthService.hasPrivInternal(currentUserPrivs, requiredPrivNames);
    }

    public static hasPrivInternal(currentUserPrivs:string, requiredPrivNames: string): boolean {
      // PJT - Needed make sure sure privName is a csv string as sometimes it is an object!!!
      if (typeof requiredPrivNames != 'string') {
        requiredPrivNames = String(requiredPrivNames);
      }
      if (!requiredPrivNames) return false;
      if (requiredPrivNames.length < 1) return false;        

      currentUserPrivs = ","+currentUserPrivs+",";

      // check each comma separated priv against user privs
      const privs = requiredPrivNames.split(',');
      let privOk = false;
      privs.forEach((requiredPriv) => {
        if (privOk == false) {
          if (currentUserPrivs.includes(","+requiredPriv.trim().toLowerCase()+",")) {
            privOk = true;
          }
        }
      });

      // admin gets access to everything, but not for 'only' privs such as Read Only or 'sysadmin'
      if (!privOk && requiredPrivNames.toLowerCase().trim() != 'orgmanager' && requiredPrivNames.toLowerCase().trim() != 'multiorgadmin') {
        privs.forEach((requiredPriv) => {
          var thisPriv = requiredPriv.trim().toLowerCase();
          if (
            privOk == false &&
            ((currentUserPrivs.includes(',admin,') &&
              !thisPriv.includes('only') &&
              !thisPriv.includes(',sysadmin,')) ||
              (currentUserPrivs.includes(',sysadmin,') &&
                !thisPriv.includes('only')))
          ) {
            privOk = true;
          }
        });
      }

      // console.log(`HasPriv(${requiredPrivNames}) for user : ${currentUserPrivs}  = ${privOk ? 'True' : 'False'}`);
      return privOk ? true : false;
    }

  isUserReadOnly(): boolean {
    return this.hasPriv('readonly') || this.hasPriv('multiorgadmin');
  }

  getClaims(): any {
    if (this.user == null) {
      return null;
    }
    if (this.user.profile == null) {
      return null;
    }
    return this.user.profile;
  }

  getUserName(): string {
    if (this.user && this.user.profile) {
      if (Array.isArray(this.user.profile['name'])) {
        return this.user.profile['name'][0];
      } else {
        return this.user.profile['name'];
      }
    } else {
      return '';
    }
  }

  getContactEmail(): string {
    return this.user && this.user.profile
      ? this.getFromProfile(this.user.profile, 'ContactEmail')
      : '';
  }

  getUserEmail(): string {
    return this.user && this.user.profile
      ? this.getUserEmailFromProfile(this.user.profile)
      : '';
  }

  getUserEmailFromProfile(profile: any): string {
    return this.getFromProfile(profile, 'email');
  }

  getFromProfile(profile: any, property: string): string {
    if (profile && property) {
      return Array.isArray(profile[property])
        ? profile[property][0]
        : profile[property];
    } else {
      return '';
    }
  }

  getClaim(claimName: string): string {
    const claims = this.getClaims();
    if (claims == null) {
      return '';
    }
    return claims[claimName];
  }

  getUserNameInitials(): string {
    const str = this.getUserName();
    const matches = str.match(/\b(\w)/g);
    const acronym = matches.join('');
    return acronym;
  }

  getAuthorizationHeaderValue(): string {
    if (this.isLoggedIn) {
      var token = this.user.token_type + ' ' + this.user.access_token;
      if (this.user.expires_in < 30) this.refreshToken();
      return token;
    } else {
      return '';
    }
  }

  // this method determine the extra parameters needed to pass to Snowstorm for correct login and selection of the correct AzureAD to use for auth.
  getLogonParams(sysLogin: boolean) {
    if (sysLogin) {
      return { login_hint: 'IFB', data: 'SubjectDetails', state: 1 };
    } else {
      const parts = location.host.toLowerCase().split('.');
      const basePathParts = this.basePath.split('.');
      debugger
      if (
        parts.length > 1 &&
        (parts[0] === basePathParts[0] || parts[0].startsWith('aabincase'))
      ) {
        return {
          login_hint: 'IntecID',
          data: this.AfterLoginNavigateTo,
          state: 1,
        };
      } else {
        const parts2 = parts[0].split('-');
        return {
          login_hint: 'ICS:' + parts2[0].toLowerCase(),
          data: this.AfterLoginNavigateTo,
          state: 1,
        };
      }
    }
  }

  startAuthentication(sysLogin: boolean): Promise<void> {
    if (this.isUserLoggedIn()) {
      return;
    }

    const args = this.getLogonParams(sysLogin);
    this.log('startAuth', args);

    if (this.PopupLoginPrompts) {
      this.manager
        .signinPopup(args)
        .then((authUser) => {
          this.userLoggedIn(authUser);
        })
        .catch((err) => {
          this.log(err);
        });
    } else {
      // This completes in the completeAuthentication() method below
      return this.manager.signinRedirect(args);
    }
  }

  completeAuthentication(): Promise<void> {
    this.log('Auth Service . complete Authentication ');

    if (this.browser.name !== 'ie' && this.browser.name !== 'edge') {
      window.location.hash = decodeURIComponent(window.location.hash);
    }
    return this.manager
      .signinRedirectCallback()
      .then((authUser) => {
        this.log('AuthService -  Auth completed');
        this.userLoggedIn(authUser);
      })
      .catch((err) => {
        this.log(err);
      });
  }

  userLoggedIn(authUser, disableDefaultRouteNav: boolean = false) {
    console.log('User Logged in', authUser);
    console.log('Access Token is good for (sec)', authUser.expires_in);
    let navTo = this.AfterLoginNavigateTo;

    // stash the current user's name...
    const currentUserName = this.user ? this.getUserName() : '';

    this.user = authUser;
    const userName = this.getUserName();

    // MRD - if username cannot be resolved/is empty then no point completing the rest of the processing - return
    if (!userName) {
      return;
    }
    const userEmail = this.getUserEmail();
    const userContactEmail = this.getContactEmail();
    this.bIsIntecUser = false;
    this.log('User logged in : ' + userName);

    // Check user privs to ensure they have  access to this product.
    // if not then jump to log out and redirect to Access Denied page.
    if (
      this.user != null &&
      (this.user.profile['INCASE365.AAB-Privs'] === undefined ||
        this.user.profile['INCASE365.AAB-Privs'] == null)
    ) {
      this.log(
        'User logged in but does not have privs to use this app - Log them out'
      );

      // Show message to user then log user out.
      const info = new MessageBoxParameters();
      info.title = 'Error';
      info.message =
        `You have successfully been authenticated as ${userEmail}, ` +
        `but your account does not contain sufficient privileges to use this application. You will now be logged out.`;
      this.dialog
        .open(MessageBoxComponent, { data: info })
        .afterClosed()
        .subscribe((d) => {
          // Log user out of application as they have no permissions to use it.
          this.logout(false);
          return;
        });
      return;
    }

    this.bUserLoggedIn = true;
    this.loggedIn.next(true);
    this.userDisplayName.next(userName);
    this.bIsIntecUser =
      userEmail &&
      userEmail.toLowerCase().startsWith('intec.') &&
      userContactEmail.toLowerCase().includes('intec');
    if (environment.production == false) this.bIsIntecUser = true;

    if (this.user.profile['Incase365.AAB-Module'])    
    {
      var mods = this.user.profile['Incase365.AAB-Module'];
      if (Array.isArray(mods))      
        this.Modules = mods;
      else
        this.Modules.push(mods);
    }
    else 
      this.Modules = [];

    if (this.user.profile['SSOAlias']) {
      this.CustomerSSOAlias = this.user.profile['SSOAlias'];
    } else {
      this.CustomerSSOAlias = '';
    }

    this.log('Token    : ' + this.getAuthorizationHeaderValue());
    this.log('User     : ' + userName);
    this.log('Customer : ' + this.getClaim('Customer'));
    this.log('Claims   : ', this.getClaims());
    this.log('bIsIntecUser : ', this.bIsIntecUser ? 'Yes' : 'No');
    if (this.Modules && this.Modules.length > 0)
      this.log('Modules      : ', this.Modules.join(', '));
    
    this.navigationService.init(this.getPrivs());

    if (this.user.state !== undefined && this.user.state !== '') {
      navTo = this.user.state;
    }

    // check stashed user name vs passed user's name
    // if they are the same then is not a new login
    // if they are not the same then assume is new login and reset the defaultDataLoaded flag
    if (currentUserName !== userName) {
      this.defaultDataRequested = false;
      this.defaultDataLoaded = false;
    }

    if (!this.defaultDataRequested) {
      this.defaultDataRequested = true;

      // new user logged in - audit the login
      setTimeout(() => {
        this.auditService.login();
      }, 1000);

      const privs = this.getPrivs();
      const fetch$ = [];
      const fetch2$ = [];
      fetch$.push(this.defaultDataService.getDefaultData());      

      this.defaultDataService
        .prefetchSyncAndUpdate()
        .pipe(take(1))
        .subscribe((res) => {
          if (res) {
            forkJoin(fetch$)
              .pipe(
                take(1),
                catchError((_) => {
                  this.defaultDataRequested = false;
                  return of(this.defaultDataRequested);
                })
              )
              .subscribe((response) => {
                if (response) {                  
                  this.defaultDataLoaded = true;
                  this.isMobile = this.breakpointObserver.isMatched('(max-width: 600px)');

                  // setup currentUserIsMultiOrgAdmin flag on environment.  
                  // This saves us having to reference the auth service to check priv which can lead to issues with circular dependencies
                  environment.currentUserIsMultiOrgAdmin = privs.toLocaleLowerCase().includes('multiorgadmin');

                  // If mobile view is enabled, redirect to mobile view
                  if (this.isMobile == true && this.defaultDataService.enableMobileView === true) {
                    if (privs.toLocaleLowerCase().includes('searchonly')) {
                      // ...yes, set the target page to search page
                      navTo = 'mobile/marker-search';
                    } else {
                      navTo = 'mobile';
                    }
                    this.router.navigate(['/' + navTo]);
                    return;
                  }

                  if (privs.toLocaleLowerCase().includes('searchonly')) {                    
                    navTo = 'marker-search/search';
                    this.router.navigate(['/' + navTo]);
                    return;
                  }
                  else if (privs.toLocaleLowerCase().includes('subjectalertimport')) {                                        
                    navTo = '/import/saimport';
                    this.router.navigate(['/' + navTo]);
                    return;
                  }
                  else {
                    // get some more data
                    fetch2$.push(this.statisticsService.getAllStatistics());                    
                    // ...task stats for the current user...
                    fetch2$.push(this.statisticsService.getUserTaskStatsData(this.getClaim('sub')));

                    forkJoin(fetch2$).pipe(take(1)).subscribe((response) => {
                      this.log(
                        'Users Default data loaded. User logged in, now navigating to ' +
                        navTo
                      );

                      this.autoLogoutService.start();
                      this.startSignalR();

                      if (
                        this.defaultDataService.askForFeedbackUrl &&
                        this.defaultDataService.askForFeedbackUrl
                      ) {
                        if (
                          !(this.hasPriv('readonly') || this.hasPriv('searchonly') || this.hasPriv('multiorgadmin'))
                        )
                          this.feedbackService.promptForFeedback(
                            this.defaultDataService.questionnaireId,
                            this.defaultDataService.askForFeedbackUrl,
                            this.defaultDataService.questionnaireTitle
                          );
                      }

                      if (
                        disableDefaultRouteNav === false ||
                        this.router.url.includes('auth-callback')
                      ) {
                        this.router.navigate(['/' + navTo]);
                      }
                    });
                  }
                }
              });
          }
        });
      //   }
      // });           
    } else {
      if (disableDefaultRouteNav === false && this.defaultDataLoaded) {
        this.router.navigate(['/' + navTo]);
      }
    }
  }

  logout(noPopup?: boolean) {
    // Stop SignalR client now as once logout takes place, if this is active then calls to EventHub will fail with 401 (Not Authorized)
    this.stopSignalR();

    // Audit logout now while we have valid access_token
    this.auditService.logout();

    const args = this.getLogonParams(false);
    let usePopup = this.PopupLoginPrompts;
    if (noPopup) {
      usePopup = noPopup;
    }
    const info = new MessageBoxParameters(
      'Please wait while you are logged out.',
      'Logout',
      MessageBoxType.None
    );

    this.dialog.open(MessageBoxComponent, { data: info }).afterClosed();

    this.userLoggedOut(null);

    if (usePopup) {      
      return this.manager.signoutPopup(args).then((user) => 
      {
        console.log('SignoutRedirect Completed');        
        this.router.navigateByUrl("/landing");
      });
    } else {
      return this.manager.signoutRedirect(args).then((user) => {
        console.log('SignoutRedirect Completed');
        this.router.navigateByUrl("/landing");
      });
    }
  }

  completeLogout(): Promise<void> {
    console.log('Complete Logout');
    return this.manager.signoutRedirectCallback().then((user) => {
      console.log('signoutRedirectCallback');
      this.userLoggedOut(null);
      window.location.href = '/landing';
    });
  }

  userLoggedOut(user) {
    console.log('userLoggedOut');
    this.autoLogoutService.stop();
    this.userService.resetCurrentUser();
    this.user = null;
    this.bUserLoggedIn = false;
    this.loggedIn.next(false);
    this.CurrentUserName = '';
  }

  public updateUserName(newUserName: string) {
    this.user.profile['name'] = newUserName;
    this.userDisplayName.next(this.getUserName());
  }

  log(message?: any, ...optionalParams: any[]) {
    if (this.bLogEnabled) {
      console.log(message, optionalParams);
    }
  }

  refreshToken(): void {
    this.manager.signinSilent();
  }

  startSignalR() {
    if (this.EnableSignalR === true) {
      setTimeout(() => {
        this.signalRService.startConnection(this);
      }, 2000);
    }
  }

  stopSignalR() {
    if (this.EnableSignalR === true) this.signalRService.endConnection();
  }
}

export function getClientSettings(
  identityURL: string,
  debug: boolean,
  allowLastLogin: boolean
): UserManagerSettings {
  const baseURL: string = 'https://' + window.location.host;
  console.log('getClientSettings', identityURL, baseURL);
  let promptForCreds = 'login';
  let popupFeatures =
    'location=no,toolbar=no,width=500,height=500,left=300,top=100';

  if (debug && debug === true) {
    popupFeatures =
      'location=yes,toolbar=yes,menubar=yes,resizable=yes,status=yes,titlebar=yes,width=500,height=500,left=300,top=100';
  }
  if (allowLastLogin && allowLastLogin === true) {
    promptForCreds = '';
  }

  // https://github.com/IdentityModel/oidc-client-js/wiki
  const settings: UserManagerSettings = {
    authority: identityURL + '/', // 'https://ics.intecdev.co.uk:5000/',
    client_id: 'AABInCase365',
    redirect_uri: baseURL + '/auth-callback', // 'https://incase.intecdev.co.uk:5010/auth-callback',
    post_logout_redirect_uri: baseURL + '/', // 'https://incase.intecdev.co.uk:5010/',
    response_type: 'id_token token',
    scope: 'openid profile InCaseAPI',
    filterProtocolClaims: false,
    loadUserInfo: true,
    prompt: promptForCreds,
    // max_age: 60, // Maximum Authentication Age. Specifies the allowable elapsed time in seconds since the last time the End-User was actively authenticated by the OP
    // monitorSession: false, This needs to be false for correct operation.
    // If true (the default) the oidc client creates lots of requests on the web client to see if the user has logout out of the IdentityServer.
    // These requests are soo many that it crashes the browser after a few minutes.

    // monitorSession: false,
    // checkSessionInterval: 30 * 1000, // in milliseconds. Check every 30 seconds
    revokeAccessTokenOnSignout: true,
    // userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    userStore: new WebStorageStateStore({ prefix:'AAB', store: window.localStorage }),
    stateStore: new WebStorageStateStore({ prefix:'AAB', store: window.localStorage }),
    automaticSilentRenew: true,
    silent_redirect_uri: baseURL + '/silent-refresh.html', // 'https://incase.intecdev.co.uk:5010/silent-refresh.html',
    popup_redirect_uri: baseURL + '/signInPopup.html', // 'https://incase.intecdev.co.uk:5010/signInPopup.html',
    popup_post_logout_redirect_uri: baseURL + '/signOutPopup.html', // 'https://incase.intecdev.co.uk:5010/signOutPopup.html'
    popupWindowFeatures: popupFeatures,
    includeIdTokenInSilentRenew: true, // Perform background token refresh
    accessTokenExpiringNotificationTime: 120, // in Seconds - Time before Access Token Expiry to trigger the use of the Refresh Token to get a new Access Token.
    silentRequestTimeout: 120 * 1000, // in Milliseonds (Default 10 seconds), 2 mins = 120 seconds
  };

  return settings;
}
